/// <amd-module name="InstantSearch/Shared/Services/InstantSearchMetadataService" />
import { ITfsPageService } from "Tfs/Platform/Page";
import { getContributedItems } from "VSS/Features/PlatformUI/ContributedItemUtil";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import * as Constants from "InstantSearch/Shared/Constants";
import { IInvokedProvidersInfo, IMenuProviderContribution, IProviderInvocationProps } from "InstantSearch/Shared/Types";

export interface IInstantSearchMetadataService extends IVssService {
    menuContributionId: string;
    providerInvocationHelpProps: IProviderInvocationProps[];
    fetchInstantSearchMenuContribution(): Promise<void>;
    getProvidersToInvoke(key: string): IInvokedProvidersInfo;
}

/**
 * Service for maintaining metadata of instant search contributions.
 */
export class InstantSearchMetadataService extends VssService implements IInstantSearchMetadataService {
    private get isProjectContext(): boolean | undefined {
        if (this._isProjectContext == undefined) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            this._isProjectContext = tfsPageData && tfsPageData.project ? true : false;
        }
        return this._isProjectContext;
    }

    /**
     * Provides menu contribution id for the instant search.
     */
    public get menuContributionId(): string {
        if (!this._menuContributionId) {
            if (this.isProjectContext) {
                // project context
                this._menuContributionId = Constants.InstantSearchProjectContextMenuContribution;
            } else {
                // collection context
                this._menuContributionId = Constants.InstantSearchCollectionContextMenuContribution;
            }
        }

        return this._menuContributionId;
    }

    /**
     * Provides list of 'provider invocation help props' used to show quick navigation help.
     */
    public get providerInvocationHelpProps(): IProviderInvocationProps[] {
        return this._providerInvocationHelpProps;
    }

    private _menuContributionId: string;
    private _invocationKeyProviderMenuIdsMap: { [key: string]: string[] } = {};
    private _invocationKeyPropsMap: { [key: string]: IProviderInvocationProps } = {};
    private _isProjectContext: boolean | undefined;
    private _providerInvocationHelpProps: IProviderInvocationProps[] = [];

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this.fetchInstantSearchMenuContribution().then(() => {
            this._initialize();
        });
    }

    /**
     * Fetches instant search menu contribution and all menu-providers targetting to it.
     */
    public fetchInstantSearchMenuContribution(): Promise<void> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getContributionAsync(this.menuContributionId);
    }

    /**
     * Provides list of menu-providers and provider's props to invoke based on searchText.
     */
    public getProvidersToInvoke(searchText: string): IInvokedProvidersInfo {
        const isQuickNavigationFFOn = isFeatureFlagEnabled(this.pageContext, "WebAccess.Search.WorkItem.QuickNavigation", false);
        const matchedProviders = isQuickNavigationFFOn ? this._invocationKeyProviderMenuIdsMap[searchText] : undefined;
        const defaultProviders = this._invocationKeyProviderMenuIdsMap["default"];
        const providerMenuIds = matchedProviders || defaultProviders;
        const key = matchedProviders ? searchText : "";
        const props = key ? this._invocationKeyPropsMap[key] : undefined;
        const invokedProvidersProps = key ? { key, props } : undefined;

        return { providerMenuIds, invokedProvidersProps };
    }

    private _initialize(): void {
        const providers = getContributedItems<IMenuProviderContribution>(
            this.pageContext,
            this.menuContributionId,
            "menuId",
            "ms.vss-web.menu-provider"
        );
        for (const instantSearchProvider of providers) {
            const { contribution } = instantSearchProvider;
            const { providerInvocationProps, menuId } = contribution;
            this._pushToInvocationKeyProvidersMap("default", menuId);
            if (providerInvocationProps && providerInvocationProps.length > 0) {
                for (const props of providerInvocationProps) {
                    const { key, quickNavigationHelpProps } = props;
                    if (quickNavigationHelpProps && !this._invocationKeyPropsMap[key]) {
                        this._providerInvocationHelpProps.push(props);
                    }
                    this._invocationKeyPropsMap[key] = props;
                    this._pushToInvocationKeyProvidersMap(key, menuId);
                }
            }
        }
    }

    private _pushToInvocationKeyProvidersMap(key: string, menuId: string): void {
        if (this._invocationKeyProviderMenuIdsMap[key]) {
            this._invocationKeyProviderMenuIdsMap[key].push(menuId);
        } else {
            this._invocationKeyProviderMenuIdsMap[key] = [menuId];
        }
    }
}

Services.add("IInstantSearchMetadataService", { serviceFactory: InstantSearchMetadataService });
